import {
    Box,
    Dialog,
    Grid,
    IconButton,
    Theme,
    Typography
} from "@mui/material";
import { GetOrderPreviewResponseBody } from "../../redux-store/types/cart";
import {
    checkImageExists,
    toINR
} from "../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { CrossIcon } from "../../assets/SvgIcons";

const amcIds = ['BDOTR', 'BPIAMC']

export default function FundsDialog(
    {
        onClose,
        open,
        data
    }: {
        onClose: () => void;
        open: boolean;
        data: GetOrderPreviewResponseBody
    }
): JSX.Element {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <Dialog
            onClose={onClose}
            open={open}
            maxWidth='md'
            sx={(theme: Theme) => ({
                '& .MuiPaper-root': {
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    width: "900px",
                },
            })}
        >
            <Box
                sx={{
                    height: "100%",
                    overflowY: "auto",
                }}
            >
                <Box
                    sx={(theme: Theme) => ({
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "20px",
                        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`
                    })}
                >
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "32.5px",
                            color: theme.palette.text.primary
                        })}
                    >
                        Funds
                    </Typography>
                    <IconButton onClick={onClose} >
                        <CrossIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "30px",
                        padding: "20px 20px 25px 20px",
                    }}
                >
                    {data?.orders?.map(obj => {
                        const checkAmcId = amcIds.includes(obj?.amcId ?? '') ? obj?.amcId?.toLowerCase() : obj?.amcId;
                        return (
                            <Box
                                key={obj?.id}
                                sx={(theme: Theme) => ({
                                    display: "flex",
                                    flexDirection: "column",
                                    '&:not(:last-child)': {
                                        borderBottom: `0.5px solid ${theme.palette.customColors.pastelGrey}`,
                                        paddingBottom: '20px',
                                    },
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        columnGap: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                                        sx={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '8px',
                                            objectFit: "contain"
                                        }}
                                        alt={obj?.fromSchemeName ?? ""}
                                    />
                                    <Typography
                                        variant="body_600"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary,
                                            lineHeight: "22.5px",
                                            letterSpacing: "0.02px"
                                        })}
                                        title={obj?.fromSchemeName ?? ""}
                                    >
                                        {obj?.fromSchemeName ?? ""}
                                    </Typography>
                                </Box>
                                <Grid container sx={{ marginTop: "10px" }} rowSpacing={2}>
                                    <Grid item container xs={12} rowSpacing={0.5}>
                                        <Grid item xs={12}>
                                            <Typography
                                                component="span"
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.text.secondary
                                                })}
                                            >
                                                {['XSIP/PAUSE', 'XSIP/CANCEL', 'STP/CANCEL', 'SWP/CANCEL'].includes(obj?.transactionType?.toUpperCase())
                                                    ? 'No. of installments'
                                                    :
                                                    ['SWITCH', 'REDEMPTION'].includes(obj?.transactionType?.toUpperCase()) ? 'Amount / Units' : 'Amount:'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                component="span"
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                })}
                                            >
                                                {['XSIP/PAUSE', 'XSIP/CANCEL', 'STP/CANCEL', 'SWP/CANCEL'].includes(obj?.transactionType?.toUpperCase())
                                                    ?
                                                    `${obj?.noOfInstallments ?? 0}`
                                                    :
                                                    ['SWITCH', 'REDEMPTION'].includes(obj?.transactionType?.toUpperCase()) ? `${toINR({ value: obj?.amount, locale, currencyCode })} / ${obj?.quantity ?? "-"}` : toINR({ value: obj?.amount, locale, currencyCode })}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid item container xs={12} rowSpacing={0.5}>
                                        <Grid item xs={12}>
                                            <Typography
                                                component="span"
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.text.secondary
                                                })}
                                            >
                                                Status:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                component="span"
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                })}
                                            >
                                                {obj?.status}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {!obj.bseOrderStatus && (
                                        <Grid item container xs={12} rowSpacing={0.5}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    component="span"
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        lineHeight: "20px",
                                                        letterSpacing: "0.02px",
                                                        color: theme.palette.text.secondary
                                                    })}
                                                >
                                                    Message:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography
                                                    component="span"
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        fontWeight: 600,
                                                        color: theme.palette.text.primary,
                                                        lineHeight: "20px",
                                                        letterSpacing: "0.02px",
                                                    })}
                                                >
                                                    {(obj?.bseOrderResponse && obj?.bseOrderResponse !== "") ?
                                                        JSON.parse(obj.bseOrderResponse)?.message ?? "" :
                                                        ""
                                                    }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Dialog>
    )
}
